import { useState, useEffect } from 'react';
import { X, FileText, Send, Wrench, Zap, Wind, Hammer, AlertTriangle, Settings, MapPin, DollarSign, CheckCircle, Info, Eye, TrendingUp, Save, Calendar, Clock } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { quoteTemplateService } from '@/services/quoteTemplateService';
import { quoteService } from '@/services/quoteService';
import { QUOTE_CATEGORIES, type QuoteTemplate } from '@/types/quoteTemplate';
import type { Job } from '@/types';
import { useGlobalToast } from '@/contexts/ToastContext';

interface QuickQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: Job;
  onQuoteSent: (quote: any) => void;
  existingQuote?: any;
}

const TIME_ESTIMATES = [
  { value: '1-2 hours', label: '1-2 hours' },
  { value: '2-4 hours', label: '2-4 hours' },
  { value: 'Half day', label: 'Half day (4 hours)' },
  { value: 'Full day', label: 'Full day (8 hours)' },
  { value: '2-3 days', label: '2-3 days' },
  { value: '1 week', label: '1 week' },
  { value: 'Custom', label: 'Custom' }
];

const TIME_SLOT_OPTIONS = [
  { value: 'morning', label: 'Morning (Before 12 PM)' },
  { value: 'afternoon', label: 'Afternoon (12 PM - 5 PM)' },
  { value: 'evening', label: 'Evening (After 5 PM)' },
  { value: 'after_11am', label: 'After 11 AM' },
  { value: 'before_2pm', label: 'Before 2 PM' },
  { value: 'flexible', label: 'Flexible (Any time)' }
];

export function QuickQuoteModal({ isOpen, onClose, job, onQuoteSent, existingQuote }: QuickQuoteModalProps) {
  const { user } = useAuthStore();
  const { success, error: showError } = useGlobalToast();
  const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
  const [suggestedTemplates, setSuggestedTemplates] = useState<QuoteTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<QuoteTemplate | null>(null);
  const [customQuote, setCustomQuote] = useState({
    price: '',
    timeEstimate: '2-4 hours',
    customTimeEstimate: '',
    description: '',
    notes: '',
    validityDays: '7',
    materialsIncluded: false,
    warranty: ''
  });
  const [activeTab, setActiveTab] = useState<'suggested' | 'all' | 'custom'>('suggested');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBudgetWarning, setShowBudgetWarning] = useState(false);
  const [priceValidation, setPriceValidation] = useState<{
    isValid: boolean;
    message: string;
    type: 'error' | 'warning' | 'success' | 'info' | null;
  }>({ isValid: true, message: '', type: null });
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [showJobDescription, setShowJobDescription] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<Array<{
    date: string;
    time_slot: string;
  }>>([
    { date: '', time_slot: '' },
    { date: '', time_slot: '' },
    { date: '', time_slot: '' }
  ]);
  const isEditMode = !!existingQuote;

  useEffect(() => {
    if (isOpen && user) {
      loadTemplates();
      loadSuggestions();
      
      // Pre-fill form if editing existing quote
      if (existingQuote) {
        setCustomQuote({
          price: existingQuote.amount.toString(),
          timeEstimate: existingQuote.estimated_duration || '2-4 hours',
          customTimeEstimate: '',
          description: existingQuote.description || '',
          notes: existingQuote.notes || '',
          validityDays: '7',
          materialsIncluded: existingQuote.materials_included || false,
          warranty: existingQuote.warranty || ''
        });
        setActiveTab('custom');
      } else {
        // Check for draft
        loadDraft();
        
        // Reset form for new quote if no draft
        if (!localStorage.getItem(`quote_draft_${job.id}`)) {
          setCustomQuote({
            price: '',
            timeEstimate: '2-4 hours',
            customTimeEstimate: '',
            description: '',
            notes: '',
            validityDays: '7',
            materialsIncluded: false,
            warranty: ''
          });
        }
      }
    }
  }, [isOpen, user, job, existingQuote]);

  const loadTemplates = async () => {
    if (!user) return;
    try {
      const userTemplates = await quoteTemplateService.getTemplatesByProvider(user.id);
      setTemplates(userTemplates.filter(t => t.isActive));
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadSuggestions = async () => {
    if (!user) return;
    try {
      const suggestions = await quoteTemplateService.suggestTemplates(
        user.id,
        job.title,
        job.description
      );
      setSuggestedTemplates(suggestions.slice(0, 3));
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    }
  };

  const getCategoryIcon = (categoryId: string) => {
    const iconMap: Record<string, any> = {
      plumbing: Wrench,
      electrical: Zap,
      hvac: Wind,
      general: Hammer,
      emergency: AlertTriangle,
      custom: Settings
    };
    return iconMap[categoryId] || FileText;
  };

  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  // Real-time price validation
  const validatePrice = (priceStr: string) => {
    if (!priceStr.trim()) {
      setPriceValidation({ isValid: false, message: '', type: null });
      return;
    }

    const budgetMin = job.budgetRange?.min || job.budgetMin || 0;
    const budgetMax = job.budgetRange?.max || job.budgetMax || 0;

    // Parse price
    let amount: number;
    if (priceStr.includes('-')) {
      const parts = priceStr.split('-');
      amount = parseFloat(parts[0].trim());
      const maxAmount = parseFloat(parts[1]?.trim() || '0');
      
      if (isNaN(amount) || isNaN(maxAmount)) {
        setPriceValidation({ 
          isValid: false, 
          message: 'Invalid range format. Use: 180-250', 
          type: 'error' 
        });
        return;
      }
      
      if (maxAmount <= amount) {
        setPriceValidation({ 
          isValid: false, 
          message: 'Max price must be higher than min price', 
          type: 'error' 
        });
        return;
      }
    } else {
      amount = parseFloat(priceStr.trim());
      if (isNaN(amount)) {
        setPriceValidation({ 
          isValid: false, 
          message: 'Please enter a valid number', 
          type: 'error' 
        });
        return;
      }
    }

    if (amount <= 0) {
      setPriceValidation({ 
        isValid: false, 
        message: 'Price must be greater than 0', 
        type: 'error' 
      });
      return;
    }

    // Check against budget
    if (budgetMax > 0) {
      if (amount > budgetMax * 1.5) {
        setPriceValidation({ 
          isValid: true, 
          message: `Much higher than budget (€${budgetMax}). May reduce chances.`, 
          type: 'warning' 
        });
      } else if (amount > budgetMax) {
        setPriceValidation({ 
          isValid: true, 
          message: `Above customer budget (€${budgetMax})`, 
          type: 'warning' 
        });
      } else if (budgetMin > 0 && amount >= budgetMin && amount <= budgetMax) {
        setPriceValidation({ 
          isValid: true, 
          message: 'Within customer budget - good fit!', 
          type: 'success' 
        });
      } else if (budgetMin > 0 && amount < budgetMin) {
        setPriceValidation({ 
          isValid: true, 
          message: `Below budget range. Consider pricing higher.`, 
          type: 'info' 
        });
      }
    } else {
      // Provide general guidance based on typical ranges
      if (amount < 50) {
        setPriceValidation({ 
          isValid: true, 
          message: 'Low price - ensure it covers your costs', 
          type: 'info' 
        });
      } else if (amount > 1000) {
        setPriceValidation({ 
          isValid: true, 
          message: 'High-value quote - ensure detailed breakdown', 
          type: 'info' 
        });
      } else {
        setPriceValidation({ 
          isValid: true, 
          message: 'Price looks reasonable', 
          type: 'success' 
        });
      }
    }
  };

  const handlePriceChange = (value: string) => {
    // Auto-format currency
    let formattedValue = value.replace(/[^\d.-]/g, ''); // Remove non-numeric except . and -
    setCustomQuote(prev => ({ ...prev, price: formattedValue }));
    validatePrice(formattedValue);
  };

  const formatCurrencyDisplay = (value: string) => {
    if (!value) return '';
    const parts = value.split('-');
    const formatted = parts.map(p => {
      const num = parseFloat(p.trim());
      return isNaN(num) ? p : num.toLocaleString('en-IE');
    });
    return formatted.join(' - ');
  };

  const handleTemplateSelect = (template: QuoteTemplate) => {
    setSelectedTemplate(template);
    const priceValue = template.maxPrice ? `${template.basePrice}-${template.maxPrice}` : template.basePrice.toString();
    setCustomQuote({
      price: priceValue,
      timeEstimate: template.timeEstimate,
      customTimeEstimate: '',
      description: template.description,
      notes: template.notes || '',
      validityDays: '7',
      materialsIncluded: template.materialsIncluded,
      warranty: template.warranty || ''
    });
    validatePrice(priceValue);
    // Show toast notification
    success('Template Applied', `"${template.name}" has been applied to your quote`);
    // Stay on current tab instead of switching to custom
  };

  const handleClearForm = () => {
    setCustomQuote({
      price: '',
      timeEstimate: '2-4 hours',
      customTimeEstimate: '',
      description: '',
      notes: '',
      validityDays: '7',
      materialsIncluded: false,
      warranty: ''
    });
    setSelectedTemplate(null);
    setPriceValidation({ isValid: true, message: '', type: null });
    success('Form Cleared', 'Starting fresh with a blank quote');
  };

  const handleSaveDraft = () => {
    // Save to localStorage for now
    const draft = {
      jobId: job.id,
      quote: customQuote,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(`quote_draft_${job.id}`, JSON.stringify(draft));
    success('Draft Saved', 'You can continue editing later');
  };

  const loadDraft = () => {
    const draftStr = localStorage.getItem(`quote_draft_${job.id}`);
    if (draftStr) {
      try {
        const draft = JSON.parse(draftStr);
        setCustomQuote(draft.quote);
        validatePrice(draft.quote.price);
        setActiveTab('custom');
        setDraftLoaded(true);
        // Show draft loaded indicator
        setTimeout(() => setDraftLoaded(false), 5000); // Hide after 5 seconds
      } catch (error) {
        console.error('Failed to load draft:', error);
      }
    }
  };

  const handleSendQuote = async () => {
    if (!customQuote.price || !customQuote.description) {
      showError('Missing Information', 'Please enter price and description');
      return;
    }

    if (!priceValidation.isValid) {
      showError('Invalid Price', priceValidation.message);
      return;
    }

    try {
      setIsSubmitting(true);

      // Parse price
      const priceStr = customQuote.price.trim();
      let amount: number;
      
      if (priceStr.includes('-')) {
        amount = parseFloat(priceStr.split('-')[0].trim());
      } else {
        amount = parseFloat(priceStr);
      }

      // Check if we need to show budget warning
      const budgetMax = job.budgetRange?.max || job.budgetMax;
      if (budgetMax && amount > budgetMax * 1.5) {
        setShowBudgetWarning(true);
        setIsSubmitting(false);
        return;
      }

      await submitQuote(amount);
    } catch (error: any) {
      console.error('Failed to submit quote:', error);
      showError(
        isEditMode ? 'Update Failed' : 'Submission Failed',
        error.message || 'Please try again'
      );
      setIsSubmitting(false);
    }
  };

  const submitQuote = async (amount: number) => {
    // Get final time estimate
    const finalTimeEstimate = customQuote.timeEstimate === 'Custom' 
      ? customQuote.customTimeEstimate 
      : customQuote.timeEstimate;

    // Filter out empty date slots
    const validDateSlots = availableSlots.filter(slot => 
      slot.date && slot.time_slot
    );

    const quoteData: any = {
      job_id: job.id,
      amount: amount,
      description: customQuote.description,
      estimated_duration: finalTimeEstimate,
      materials_included: customQuote.materialsIncluded,
      warranty: customQuote.warranty,
      notes: customQuote.notes,
      valid_until_days: parseInt(customQuote.validityDays) || 7
    };

    // Add available_date_slots if at least one is filled
    if (validDateSlots.length > 0) {
      quoteData.available_date_slots = validDateSlots;
    }

    // Track template usage
    if (selectedTemplate && !isEditMode) {
      await quoteTemplateService.useTemplate(selectedTemplate.id);
    }

    // Submit or update quote
    let result;
    if (isEditMode && existingQuote) {
      result = await quoteService.updateQuote(existingQuote.id, {
        amount: amount,
        description: customQuote.description,
        estimated_duration: finalTimeEstimate
      });
      success('Quote Updated', 'Your quote has been updated successfully');
    } else {
      result = await quoteService.submitQuote(quoteData);
      success('Quote Sent', 'Your quote has been sent to the customer');
      // Clear draft after successful submission
      localStorage.removeItem(`quote_draft_${job.id}`);
    }

    onQuoteSent(result);
    onClose();
    setIsSubmitting(false);
  };

  const handleBudgetWarningConfirm = async () => {
    setShowBudgetWarning(false);
    setIsSubmitting(true);
    try {
      const amount = parseFloat(customQuote.price.split('-')[0].trim());
      await submitQuote(amount);
    } catch (error: any) {
      console.error('Failed to submit quote:', error);
      showError(
        isEditMode ? 'Update Failed' : 'Submission Failed',
        error.message || 'Please try again'
      );
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const budgetMin = job.budgetRange?.min || job.budgetMin || 0;
  const budgetMax = job.budgetRange?.max || job.budgetMax || 0;
  const descriptionLength = customQuote.description.length;
  const notesLength = customQuote.notes.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditMode ? 'Edit Quote' : 'Send Quote'}
              </h2>
              {draftLoaded && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full animate-pulse">
                  Draft Loaded
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1">{job.title}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Left Panel - Template Selection */}
          <div className="w-full md:w-1/2 border-b md:border-b-0 md:border-r border-gray-200 overflow-y-auto">
            <div className="p-6">
              {/* Tabs */}
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
                <button
                  onClick={() => setActiveTab('suggested')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'suggested'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Suggested ({suggestedTemplates.length})
                </button>
                <button
                  onClick={() => setActiveTab('all')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'all'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  All Templates
                </button>
                <button
                  onClick={() => setActiveTab('custom')}
                  className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === 'custom'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Custom Quote
                </button>
              </div>

              {/* Category Filter for All Templates */}
              {activeTab === 'all' && (
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                      selectedCategory === 'all'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    All
                  </button>
                  {QUOTE_CATEGORIES.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Template List */}
              <div className="space-y-3">
                {activeTab === 'suggested' && suggestedTemplates.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No suggested templates</p>
                    <p className="text-xs text-gray-400 mt-1">Try the "All Templates" tab</p>
                  </div>
                )}

                {activeTab === 'suggested' && suggestedTemplates.map((template) => {
                  const CategoryIcon = getCategoryIcon(template.category);
                  return (
                    <div
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id
                          ? 'border-primary-500 bg-primary-50 shadow-sm ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start">
                        <CategoryIcon className="w-5 h-5 text-primary-600 mt-1 mr-3 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900">{template.name}</h4>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{template.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm font-medium text-gray-900">
                              €{template.basePrice}
                              {template.maxPrice && template.maxPrice !== template.basePrice && ` - €${template.maxPrice}`}
                            </span>
                            <span className="text-xs text-green-600 font-medium">{template.successRate}% accepted</span>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span>⏱️ {template.timeEstimate}</span>
                            <span>📦 {template.materialsIncluded ? 'Materials incl.' : 'Materials extra'}</span>
                            {template.warranty && <span>✓ {template.warranty}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {activeTab === 'all' && filteredTemplates.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">No templates found</p>
                    <p className="text-xs text-gray-400 mt-1">Create templates to speed up quoting</p>
                  </div>
                )}

                {activeTab === 'all' && filteredTemplates.map((template) => {
                  const CategoryIcon = getCategoryIcon(template.category);
                  return (
                    <div
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id
                          ? 'border-primary-500 bg-primary-50 shadow-sm ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start">
                        <CategoryIcon className="w-5 h-5 text-primary-600 mt-1 mr-3 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900">{template.name}</h4>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{template.description}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm font-medium text-gray-900">
                              €{template.basePrice}
                              {template.maxPrice && template.maxPrice !== template.basePrice && ` - €${template.maxPrice}`}
                            </span>
                            <span className="text-xs text-gray-500">{template.usageCount > 0 ? `Used ${template.usageCount}x` : 'New template'}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span>⏱️ {template.timeEstimate}</span>
                            <span>📦 {template.materialsIncluded ? 'Materials incl.' : 'Materials extra'}</span>
                            {template.warranty && <span>✓ {template.warranty}</span>}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {activeTab === 'custom' && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900 mb-1">Custom Quote</h4>
                        <p className="text-sm text-blue-700">
                          Fill in the form on the right to create a custom quote for this job.
                          {templates.length > 0 && ' Or select a template above to start with a pre-filled quote.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Quote Details */}
          <div className="w-full md:w-1/2 p-6 overflow-y-auto">
            {/* Job Context Card */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-4 rounded-lg mb-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-600" />
                Job Details
              </h4>
              <div className="space-y-2 text-sm">
                {(budgetMin || budgetMax) && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Budget:</strong> €{budgetMin || '?'} - €{budgetMax || '?'}
                    </span>
                  </div>
                )}
                {job.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Location:</strong> {job.location}
                    </span>
                  </div>
                )}
                {job.urgency && (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Urgency:</strong> {job.urgency}
                    </span>
                  </div>
                )}
                {job.description && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <p className={`text-gray-600 text-xs ${!showJobDescription ? 'line-clamp-3' : ''}`}>
                      {job.description}
                    </p>
                    {job.description.length > 150 && (
                      <button
                        onClick={() => setShowJobDescription(!showJobDescription)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
                      >
                        {showJobDescription ? 'Show less' : 'Read more'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Template Selected Confirmation */}
            {selectedTemplate && (
              <div className="bg-green-50 border border-green-200 p-3 rounded-lg mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-sm text-green-800">
                  Using template: <strong>{selectedTemplate.name}</strong>
                </p>
              </div>
            )}

            <h3 className="text-lg font-medium text-gray-900 mb-1">Quote Details</h3>
            {selectedTemplate && (
              <p className="text-xs text-gray-500 mb-4">
                Based on template: <span className="font-medium text-gray-700">{selectedTemplate.name}</span>
                {' • '}
                <button onClick={handleClearForm} className="text-primary-600 hover:text-primary-700 font-medium">
                  Clear & start over
                </button>
              </p>
            )}
            {!selectedTemplate && (customQuote.price || customQuote.description) && (
              <p className="text-xs text-gray-500 mb-4">
                <button onClick={handleClearForm} className="text-primary-600 hover:text-primary-700 font-medium">
                  Clear form & start over
                </button>
              </p>
            )}
            
            <div className="space-y-4">
              {/* Always show budget reference */}
              {budgetMin > 0 && budgetMax > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                    <span className="font-medium text-gray-700">Customer Budget Range</span>
                    <span className="font-semibold text-gray-900">€{budgetMin.toLocaleString()} - €{budgetMax.toLocaleString()}</span>
                  </div>
                  <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="absolute h-full bg-blue-300 w-full" />
                    {customQuote.price && priceValidation.isValid && (() => {
                      const price = parseFloat(customQuote.price.split('-')[0]);
                      if (!isNaN(price)) {
                        const position = Math.min(100, (price / (budgetMax * 1.5)) * 100);
                        return (
                          <div 
                            className={`absolute h-full w-1 ${
                              price <= budgetMax ? 'bg-green-500' : 'bg-yellow-500'
                            }`}
                            style={{ left: `${position}%` }}
                          >
                            <div className={`absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium whitespace-nowrap ${
                              price <= budgetMax ? 'text-green-600' : 'text-yellow-600'
                            }`}>
                              Your quote
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price (€) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-500">€</span>
                  <input
                    type="text"
                    value={customQuote.price}
                    onChange={(e) => handlePriceChange(e.target.value)}
                    placeholder="180 or 180-250"
                    className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      priceValidation.type === 'error' ? 'border-red-500' : 
                      priceValidation.type === 'warning' ? 'border-yellow-500' :
                      priceValidation.type === 'success' ? 'border-green-500' :
                      'border-gray-300'
                    }`}
                  />
                  {customQuote.price && (
                    <div className="absolute right-3 top-2.5 text-sm text-gray-500">
                      {formatCurrencyDisplay(customQuote.price)}
                    </div>
                  )}
                </div>
                
                {/* Real-time validation feedback */}
                {priceValidation.message && (
                  <div className={`flex items-center gap-2 mt-2 text-sm ${
                    priceValidation.type === 'error' ? 'text-red-600' :
                    priceValidation.type === 'warning' ? 'text-yellow-600' :
                    priceValidation.type === 'success' ? 'text-green-600' :
                    'text-blue-600'
                  }`}>
                    {priceValidation.type === 'error' && <AlertTriangle className="w-4 h-4" />}
                    {priceValidation.type === 'warning' && <AlertTriangle className="w-4 h-4" />}
                    {priceValidation.type === 'success' && <CheckCircle className="w-4 h-4" />}
                    {priceValidation.type === 'info' && <Info className="w-4 h-4" />}
                    <span>{priceValidation.message}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time Estimate
                </label>
                <select
                  value={customQuote.timeEstimate}
                  onChange={(e) => setCustomQuote(prev => ({ ...prev, timeEstimate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {TIME_ESTIMATES.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {customQuote.timeEstimate === 'Custom' && (
                  <input
                    type="text"
                    value={customQuote.customTimeEstimate}
                    onChange={(e) => setCustomQuote(prev => ({ ...prev, customTimeEstimate: e.target.value }))}
                    placeholder="e.g., 3-5 business days"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent mt-2"
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  💡 Tip: Similar {job.category || 'jobs'} typically take 2-4 hours
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quote Valid For
                  </label>
                  <select
                    value={customQuote.validityDays}
                    onChange={(e) => setCustomQuote(prev => ({ ...prev, validityDays: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="3">3 days</option>
                    <option value="7">7 days</option>
                    <option value="14">14 days</option>
                    <option value="30">30 days</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Materials
                  </label>
                  <select
                    value={customQuote.materialsIncluded ? 'included' : 'extra'}
                    onChange={(e) => setCustomQuote(prev => ({ ...prev, materialsIncluded: e.target.value === 'included' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="included">Included in price</option>
                    <option value="extra">Charged separately</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Warranty (Optional)
                </label>
                <input
                  type="text"
                  value={customQuote.warranty}
                  onChange={(e) => setCustomQuote(prev => ({ ...prev, warranty: e.target.value }))}
                  placeholder="e.g., 12 months parts & labor"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={customQuote.description}
                  onChange={(e) => setCustomQuote(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe what's included in this quote...&#10;&#10;💡 Tip: Include:&#10;• Labor and time breakdown&#10;• Materials (if applicable)&#10;• Cleanup and disposal&#10;• Any guarantees or follow-up"
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className={`text-xs mt-1 ${
                  descriptionLength > 450 ? 'text-red-600 font-medium' :
                  descriptionLength > 400 ? 'text-yellow-600' :
                  'text-gray-500'
                }`}>
                  {descriptionLength}/500 characters
                  {descriptionLength > 450 && ' - Almost at limit!'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes
                </label>
                <textarea
                  value={customQuote.notes}
                  onChange={(e) => setCustomQuote(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional information or terms..."
                  rows={3}
                  maxLength={300}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className={`text-xs mt-1 ${
                  notesLength > 270 ? 'text-red-600 font-medium' :
                  notesLength > 240 ? 'text-yellow-600' :
                  'text-gray-500'
                }`}>
                  {notesLength}/300 characters
                  {notesLength > 270 && ' - Almost at limit!'}
                </p>
              </div>

              {/* Available Date/Time Slots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Available Date & Time Slots (Optional - Provide 3 options)
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Customer will select one of these dates when accepting your quote
                </p>
                <div className="space-y-3">
                  {availableSlots.map((slot, index) => (
                    <div key={index} className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Date {index + 1}
                        </label>
                        <input
                          type="date"
                          value={slot.date}
                          onChange={(e) => {
                            const newSlots = [...availableSlots];
                            newSlots[index].date = e.target.value;
                            setAvailableSlots(newSlots);
                          }}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          Time Preference
                        </label>
                        <select
                          value={slot.time_slot}
                          onChange={(e) => {
                            const newSlots = [...availableSlots];
                            newSlots[index].time_slot = e.target.value;
                            setAvailableSlots(newSlots);
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          <option value="">Select time...</option>
                          {TIME_SLOT_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Providing multiple date options increases the chance of acceptance
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            {!isEditMode && (
              <button
                onClick={handleSaveDraft}
                disabled={isSubmitting || (!customQuote.price && !customQuote.description)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                Save Draft
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPreview(true)}
              disabled={!customQuote.price || !customQuote.description || isSubmitting}
              className="px-4 py-2 border border-primary-300 rounded-lg text-primary-700 hover:bg-primary-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button
              onClick={handleSendQuote}
              disabled={!customQuote.price || !customQuote.description || !priceValidation.isValid || isSubmitting}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {isEditMode ? 'Updating...' : 'Sending...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {isEditMode ? 'Update Quote' : 'Send Quote'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Quote Preview</h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="bg-gradient-to-br from-primary-50 to-blue-50 p-6 rounded-lg border border-primary-200 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="text-2xl font-bold text-gray-900">
                      €{formatCurrencyDisplay(customQuote.price)}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      Valid for {customQuote.validityDays} days
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Time Estimate</p>
                    <p className="font-semibold text-gray-900">
                      {customQuote.timeEstimate === 'Custom' ? customQuote.customTimeEstimate : customQuote.timeEstimate}
                    </p>
                  </div>
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Job:</strong> {job.title}
                </div>
              </div>

              {/* Budget comparison in preview */}
              {budgetMin > 0 && budgetMax > 0 && customQuote.price && priceValidation.isValid && (() => {
                const price = parseFloat(customQuote.price.split('-')[0]);
                if (!isNaN(price)) {
                  const withinBudget = price <= budgetMax;
                  return (
                    <div className={`p-4 rounded-lg border mb-6 ${
                      withinBudget ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        {withinBudget ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Info className="w-5 h-5 text-yellow-600" />
                        )}
                        <span className={`font-medium ${
                          withinBudget ? 'text-green-900' : 'text-yellow-900'
                        }`}>
                          {withinBudget ? 'Within Customer Budget' : 'Above Customer Budget'}
                        </span>
                      </div>
                      <p className={`text-sm ${
                        withinBudget ? 'text-green-700' : 'text-yellow-700'
                      }`}>
                        Customer budget: €{budgetMin.toLocaleString()} - €{budgetMax.toLocaleString()}
                      </p>
                    </div>
                  );
                }
                return null;
              })()}

              <div className="space-y-4">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">What's Included</h5>
                  <p className="text-gray-700 whitespace-pre-wrap">{customQuote.description}</p>
                </div>

                {customQuote.notes && (
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2">Additional Notes</h5>
                    <p className="text-gray-700 whitespace-pre-wrap">{customQuote.notes}</p>
                  </div>
                )}

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h5 className="font-semibold text-gray-900 mb-2">Terms & Conditions</h5>
                  <div className="space-y-1 text-sm text-gray-700">
                    <p>• Materials: {customQuote.materialsIncluded ? 'Included in price' : 'Charged separately'}</p>
                    {customQuote.warranty && <p>• Warranty: {customQuote.warranty}</p>}
                    <p>• Quote valid for: {customQuote.validityDays} days from acceptance</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  handleSendQuote();
                }}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                Send Quote
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Warning Modal */}
      {showBudgetWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Quote Above Budget
                  </h3>
                  <p className="text-gray-700 mb-4">
                    Your quote (€{customQuote.price.split('-')[0]}) is significantly higher than the customer's budget (€{budgetMin}-€{budgetMax}).
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Note:</strong> High quotes may reduce your chances of being selected. Consider adjusting your price or providing detailed justification in your description.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowBudgetWarning(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition-colors"
              >
                Go Back & Edit
              </button>
              <button
                onClick={handleBudgetWarningConfirm}
                className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                Send Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
